import { Subject, filter, map, Observable } from 'rxjs'
import { shareReplay, tap } from 'rxjs/operators'
import CableApp from 'actioncable'

// Declare window.gon for TypeScript
declare global {
    interface Window {
        gon?: {
            currentUser?: {
                id: string | number
            }
        }
    }
}

interface EventBusMessage {
    topic: string
    payload: any
    timestamp: number
}

interface FollowRequest {
    resource_type: 'answer' | 'quest' | 'user'
    resource_id: string
}

class EventBusService {
    private eventBus$ = new Subject<EventBusMessage>()
    private channel: any = null
    private subscribedResources = new Set<string>()
    private connectionCallbacks = new Set<() => void>()

    constructor() {
        // Set up global logging for ALL EventBus messages
        this.eventBus$.subscribe({
            next: message => {
                // Only log workflow status messages, not all EventBus traffic
                if (message.topic === 'mint_ordinal_status' && message.payload?.status?.message) {
                    console.log(`Minting:`, message.payload.status.message)
                }
            },
            error: err => {
                console.error(`🚌 [EventBus] Error in message stream:`, err)
            },
        })
    }

    connect(initialResources?: { answer_ids?: string[]; quest_ids?: string[] }) {
        if (this.channel) {
            return
        }

        // Get current user ID from window.gon (standard pattern in this app)
        const userId = window.gon?.currentUser?.id
        if (!userId) {
            console.error(
                '🚌 [EventBus] Cannot connect - no user ID found in window.gon.currentUser',
            )
            return
        }

        const params: any = {
            channel: 'EventBusChannel',
            user_id: userId, // Pass user_id like other channels in this app
        }
        if (initialResources?.answer_ids) {
            params.answer_ids = initialResources.answer_ids
        }
        if (initialResources?.quest_ids) {
            params.quest_ids = initialResources.quest_ids
        }

        this.channel = CableApp.cable.subscriptions.create(params, {
            received: (data: any) => {
                // All messages should have a topic - inject into RxJS Subject
                if (data.topic) {
                    this.eventBus$.next(data as EventBusMessage)
                } else {
                    console.warn('🚌 [EventBus] Received data without topic:', data)
                }
            },

            connected: () => {
                console.log('✅ [EventBus] Connected')
                this.connectionCallbacks.forEach(cb => cb())
            },

            disconnected: () => {
                console.log('❌ [EventBus] Disconnected')
                this.subscribedResources.clear()
            },

            rejected: () => {
                console.error('🚫 [EventBus] Subscription rejected by server!')
            },
        })
    }

    disconnect() {
        if (this.channel) {
            this.channel.unsubscribe()
            this.channel = null
            this.subscribedResources.clear()
            console.log('❌ [EventBus] Disconnected')
        }
    }

    onConnected(callback: () => void) {
        this.connectionCallbacks.add(callback)
        // If already connected, call immediately
        if (this.channel?.consumer?.connection?.isOpen()) {
            callback()
        }
    }

    // Get observable for specific topic
    getTopic$(topic: string): Observable<any> {
        return this.eventBus$.pipe(
            filter(msg => msg.topic === topic),
            map(msg => msg.payload),
            shareReplay({ bufferSize: 1, refCount: true }),
        )
    }

    // Get observable for specific topic and resource
    getResourceTopic$(topic: string, resourceType: string, resourceId: string): Observable<any> {
        return this.eventBus$.pipe(
            filter(msg => msg.topic === topic),
            map(msg => msg.payload),
            filter(payload => {
                // Check if payload is for this specific resource
                const resourceKey = `${resourceType}_id`
                return (
                    payload[resourceKey] === resourceId ||
                    payload[resourceKey] === parseInt(resourceId)
                )
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        )
    }

    // Get raw event bus for custom filtering
    getEventBus$(): Observable<EventBusMessage> {
        return this.eventBus$.asObservable()
    }

    isConnected(): boolean {
        return !!this.channel?.consumer?.connection?.isOpen()
    }

    isSubscribedTo(resourceType: string, resourceId: string): boolean {
        const key = `${resourceType}_${resourceId}`
        return this.subscribedResources.has(key)
    }
}

// Singleton instance
export const eventBusService = new EventBusService()

// Expose to window for debugging
if (typeof window !== 'undefined') {
    ;(window as any).eventBusService = eventBusService
}
